import { cellAddress } from "./a1";
import { FormatRun } from "./workbookModel";

// Number formats, summarised by column.
//
// Lower stakes than the formula compression but the same reasoning: 50,000 cells
// formatted as currency is one fact. What the model actually needs is "column D is
// money, column E is a date" -- so this reports each column's dominant format and the
// cells that differ from it, rather than a run per contiguous block.
//
// Dominant-plus-exceptions rather than true run-length because a column's formatting
// is usually uniform with a handful of stragglers (a total row in bold currency, a
// stray General cell someone typed over). Runs would describe that as three or four
// entries; this describes it as one plus the stragglers, which is both smaller and
// closer to how a person would say it.

/** Formats carrying no information worth a line in the prompt. */
const UNREMARKABLE = new Set(["General", "@", ""]);

/**
 * Summarises one rectangle of number formats.
 *
 * `grid` is row-major, as the Excel API returns it. Columns whose dominant format is
 * unremarkable are omitted entirely -- unless individual cells within them differ, in
 * which case those cells are worth reporting on their own.
 */
export function summariseFormats(grid: string[][], startRow = 0, startColumn = 0): FormatRun[] {
  const rowCount = grid.length;
  if (rowCount === 0) return [];
  const columnCount = Math.max(...grid.map((row) => row.length));

  const runs: FormatRun[] = [];

  for (let column = 0; column < columnCount; column += 1) {
    const counts = new Map<string, number>();
    for (let row = 0; row < rowCount; row += 1) {
      const format = grid[row][column] ?? "";
      counts.set(format, (counts.get(format) ?? 0) + 1);
    }

    // The dominant format, with ties broken towards the first seen so the result is
    // stable across extracts rather than flapping between equally common formats.
    let dominant = "";
    let best = -1;
    for (const [format, count] of counts) {
      if (count > best) {
        dominant = format;
        best = count;
      }
    }

    const absoluteColumn = startColumn + column;
    const worthReporting = !UNREMARKABLE.has(dominant);

    if (worthReporting) {
      runs.push({
        range:
          rowCount === 1
            ? cellAddress(startRow, absoluteColumn)
            : `${cellAddress(startRow, absoluteColumn)}:${cellAddress(startRow + rowCount - 1, absoluteColumn)}`,
        format: dominant,
      });
    }

    // Stragglers: cells whose format differs from their column's. Reported whether or
    // not the dominant format was, because "this one cell is a date in a column of
    // numbers" is exactly the kind of thing that is wrong in a real workbook.
    for (let row = 0; row < rowCount; row += 1) {
      const format = grid[row][column] ?? "";
      if (format !== dominant && !UNREMARKABLE.has(format)) {
        runs.push({ range: cellAddress(startRow + row, absoluteColumn), format });
      }
    }
  }

  return runs;
}
